import { motion } from "framer-motion"
import { Building2, Clock, Package, Sparkles, TrendingUp, Users } from "lucide-react"
import { IMAGES } from "@/assets/images"
import { fadeInUp, staggerContainer, staggerItem, springPresets } from "@/lib/motion"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { ROUTE_PATHS } from "@/lib/index"

const features = [
  {
    icon: Clock,
    titleAr: "تنفيذ احترافي",
    descriptionAr: "نقدم خدمات ضيافة وتنفيذ بمعايير عالية تناسب جميع المناسبات.",
  },
  {
    icon: Sparkles,
    titleAr: "تغليف بالشعار",
    descriptionAr: "نوفر خدمة التغليف المخصص بشعار شركتك",
  },
  {
    icon: TrendingUp,
    titleAr: "كميات كبيرة",
    descriptionAr: "قدرة إنتاجية عالية لتلبية احتياجات الشركات الكبرى",
  },
  {
    icon: Building2,
    titleAr: "دقة في التوقيت",
    descriptionAr: "التزام تام بمواعيد التسليم دون تأخير",
  },
  {
    icon: Package,
    titleAr: "تنوع في القوائم",
    descriptionAr: "قوائم متنوعة تناسب جميع المناسبات والاجتماعات",
  },
  {
    icon: Users,
    titleAr: "خدمة مرنة",
    descriptionAr: "نتكيف مع احتياجاتكم ومتطلباتكم الخاصة",
  },
]

const packages = [
  {
    id: "basic",
    nameAr: "الباقة الأساسية",
    price: "٨٥",
    features: [
      "كوفي بريك يومي",
      "مخبوزات طازجة",
      "مشروبات ساخنة",
      "خدمة توصيل",
      "تغليف احترافي",
    ],
  },
  {
    id: "premium",
    nameAr: "الباقة المتميزة",
    price: "١٢٥",
    featured: true,
    features: [
      "كل مميزات الباقة الأساسية",
      "حلويات فاخرة",
      "سلطات ومقبلات",
      "تغليف بالشعار",
      "مشرف خدمة",
      "أطباق ساخنة",
    ],
  },
  {
    id: "vip",
    nameAr: "الباقة البريميوم",
    price: "٢٠٠",
    features: [
      "كل مميزات الباقة المتميزة",
      "قائمة مخصصة",
      "شيف خاص",
      "ديكور وتنسيق",
      "خدمة كاملة",
      "تصوير احترافي",
      "منسق فعاليات",
    ],
  },
]

const galleryImages = [
  { src: IMAGES.CORPORATE_CATERING_1, titleAr: "اجتماع تنفيذي" },
  { src: '/images/WhatsApp Image 2026-05-10 at 12.36.04 AM.jpeg', titleAr: "كوفي بريك فاخر" },
  { src: '/images/WhatsApp Image 2026-05-10 at 12.43.02 AM.jpeg', titleAr: "مأدبة شركات" },
  { src: IMAGES.CORPORATE_CATERING_6, titleAr: "ضيافة راقية" },
  { src: IMAGES.CORPORATE_CATERING_8, titleAr: "فعالية شركات" },
  { src: IMAGES.CORPORATE_CATERING_9, titleAr: "استقبال رسمي" },
]

export default function CorporateCatering() {
  return (
    <div className="min-h-screen" dir="rtl">
        <section className="relative h-[70vh] lg:h-[80vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.CORPORATE_CATERING_7}
              alt="كيترنج الشركات"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <motion.div
            className="relative z-10 container mx-auto px-4 text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1
              className="text-5xl lg:text-7xl font-bold text-foreground mb-6"
              variants={fadeInUp}
              style={{ fontFamily: "Cairo, Tajawal, sans-serif" }}
            >
              كيترنج الشركات الاحترافي
            </motion.h1>
            <motion.p
              className="text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8"
              variants={fadeInUp}
            >
              نخدم الشركات والمؤسسات بحلول ضيافة متكاملة، من الاجتماعات اليومية إلى
              الفعاليات الكبرى
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to={ROUTE_PATHS.CONTACT}>
                <Button
                  size="lg"
                  className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-300 text-lg px-8 py-6"
                >
                  اطلب عرض سعر
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </section>

        <section className="py-20 lg:py-32 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
                لماذا نحن الخيار الأمثل
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                نقدم خدمات كيترنج احترافية تليق بمكانة شركتك
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              {features.map((feature, index) => {
                const Icon = feature.icon
                return (
                  <motion.div key={index} variants={staggerItem}>
                    <Card className="p-8 h-full bg-card hover:shadow-lg transition-all duration-500 border-border">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-6">
                          <Icon className="w-8 h-8 text-accent" />
                        </div>
                        <h3 className="text-2xl font-bold text-foreground mb-3">
                          {feature.titleAr}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {feature.descriptionAr}
                        </p>
                      </div>
                    </Card>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
                معرض أعمالنا
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                نماذج من خدماتنا للشركات والمؤسسات
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              {galleryImages.map((image, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  whileHover={{ scale: 1.03 }}
                  transition={springPresets.snappy}
                  className="relative group overflow-hidden rounded-lg aspect-[4/3] cursor-pointer"
                >
                  <img
                    src={image.src}
                    alt={image.titleAr}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                    <h3 className="text-xl font-bold text-white">
                      {image.titleAr}
                    </h3>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
                باقاتنا المميزة
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                اختر الباقة التي تناسب احتياجات شركتك
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              {packages.map((pkg) => (
                <motion.div key={pkg.id} variants={staggerItem}>
                  <Card
                    className={`p-8 h-full relative overflow-hidden ${
                      pkg.featured
                        ? "border-2 border-accent shadow-xl scale-105"
                        : "border-border"
                    }`}
                  >
                    {pkg.featured && (
                      <div className="absolute top-0 left-0 right-0 bg-accent text-accent-foreground text-center py-2 text-sm font-bold">
                        الأكثر طلباً
                      </div>
                    )}
                    <div className={pkg.featured ? "mt-8" : ""}>
                      <h3 className="text-2xl font-bold text-foreground mb-4">
                        {pkg.nameAr}
                      </h3>
                      <div className="mb-6">
                        <span className="text-5xl font-bold text-primary">
                          {pkg.price}
                        </span>
                        <span className="text-muted-foreground mr-2">
                          ريال / شهرياً
                        </span>
                      </div>
                      <ul className="space-y-3 mb-8">
                        {pkg.features.map((feature, index) => (
                          <li
                            key={index}
                            className="flex items-start text-muted-foreground"
                          >
                            <span className="text-accent ml-2 mt-1">✓</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Link to={ROUTE_PATHS.CONTACT}>
                        <Button
                          className={`w-full ${
                            pkg.featured
                              ? "bg-accent text-accent-foreground hover:bg-accent/90"
                              : "bg-primary text-primary-foreground hover:bg-primary/90"
                          }`}
                          size="lg"
                        >
                          اطلب الباقة
                        </Button>
                      </Link>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-20 lg:py-32 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center max-w-3xl mx-auto"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                جاهزون لخدمة شركتك
              </h2>
              <p className="text-xl mb-8 opacity-90">
                تواصل معنا اليوم واحصل على عرض سعر مخصص يناسب احتياجاتك
              </p>
              <Link to={ROUTE_PATHS.CONTACT}>
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6"
                >
                  تواصل معنا الآن
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
  )
}
